import React from 'react';
import styled from 'styled-components';
import { fade } from '@material-ui/core';
import { TypographyProps as BaseTypographyProps, Card, CardProps, Typography } from '../../../ui-kit';
import { Color } from '../../../generated/graphql';
import { enumToPrimaryColorMap } from '../../../utils';

type TypographyProps = Omit<BaseTypographyProps, 'variant' | 'component' | 'ref'>;

interface SolidFill {
    solidFill?: boolean;
}

export const FancyCard = styled.div<SolidFill>`
    ${({ solidFill, theme, onClick }) => `
        background: ${solidFill ? `#554DF0` : `linear-gradient(270deg, #2EF9F9 0%, #554DF0 100%)`};
        border-radius: ${theme.shape.borderRadius}px;
        ${onClick
        ? `
            :hover {
                cursor: pointer;
            }
        `
        : ``}
    `}
    > * {
        background: -webkit-linear-gradient(180deg, rgba(255,255,255,1) 0%, rgba(0,212,255,1) 100%);
        -webkit-background-clip: text;
        background-clip: text;
        -webkit-text-fill-color: transparent;
    }
    display: flex;
    justify-content: center;
    align-items: center;
    ${({ theme }) => `
        height: ${theme.spacing(15)}px;
        margin-bottom: ${theme.spacing(1.25)}px;
        padding: ${theme.spacing(1.5)}px;
    `}
`;

export const CardPlaceholder = styled(
    React.forwardRef<HTMLHeadingElement, Omit<TypographyProps, 'innerRef'>>(
        (props, ref) => <Typography {...props} variant='h6' component='h3' innerRef={ref} />)
)`
    ${({ theme }) => `
        && {
            font-size: ${theme.typography.pxToRem(24)};
            font-weight: ${theme.typography.fontWeightBold};
        }
    `}
`;

export const HugeDashboardCardTitle = styled(
    React.forwardRef<HTMLHeadingElement, Omit<TypographyProps, 'innerRef'>>(
        (props, ref) => <Typography {...props} variant='h1' component='h3' innerRef={ref} />)
)`
    ${({ theme }) => `
        && {
            font-size: ${theme.typography.pxToRem(64)};
            font-weight: ${theme.typography.fontWeightBold};
        }
    `}
`;


const LegendCircle = styled.div`
    display: block;
    border-radius: 50%;
    width: 18px;
    height: 18px;
`;

export const CashLegendCircle = styled(LegendCircle)`
    background-color: #FF9CD7;
`;

export const CreditCardLegendCircle = styled(LegendCircle)`
    background-color: #2EF9F9;
`;

export const TotalLegendCircle = styled(LegendCircle)`
    background-color: #554DF0;
`;


export const LegendItemText = styled(
    React.forwardRef<HTMLHeadingElement, Omit<TypographyProps, 'innerRef'>>(
        (props, ref) => <Typography {...props} variant='subtitle1' innerRef={ref} />)
)`
    ${({ theme }) => `
        && {
            font-size: ${theme.typography.pxToRem(18)};
            font-weight: ${theme.typography.fontWeightRegular};
        }
    `}
`;

export const LegendTitle = styled(
    React.forwardRef<HTMLHeadingElement, Omit<TypographyProps, 'innerRef'>>(
        (props, ref) => <Typography {...props} variant='h2' innerRef={ref} />)
)`
    ${({ theme }) => `
        && {
            font-size: ${theme.typography.pxToRem(18)};
            font-weight: ${theme.typography.fontWeightBold};
        }
    `}
`;

interface ColoredCardProps extends CardProps {
    color?: Color;
    onClick?: () => void;
}

export const ColoredCard = styled(({ color, ...props }: ColoredCardProps) => <Card {...props} />)`
    ${({ theme, color = Color.Blue, onClick }) => `
        &.MuiPaper-root {
            background-color: ${fade(enumToPrimaryColorMap(theme, color), 0.16)};
        }
        ${onClick ? `&:hover { cursor: pointer; }` : ``}
    `}
`;

export interface ItemsPerRow {
    itemsPerRow: number;
}
export const ResponsiveColumnsContainer = styled.div<ItemsPerRow>`
    display: grid;
    gap: 20px;
    ${({ theme, itemsPerRow }) => `
            grid-template-columns: minmax(0, 1fr);
        ${theme.breakpoints.up('md')} {
            grid-template-columns: repeat(${itemsPerRow}, minmax(0, 1fr));
        }
    `}
`;

export const DashboardRowWrapper = styled.div`
    width: 100%;
    display: grid;
    grid-auto-rows: auto 1fr;
    height: 100%;
    ${({ theme }) => `
        gap: ${theme.spacing(2)}px ${theme.spacing(2.5)}px;
        padding: ${theme.spacing(3)}px;
    `}
`;